import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LoginForm } from './admin/LoginForm';
import { Sidebar } from './admin/Sidebar';
import { DashboardViewNew } from './admin/DashboardViewNew';
import { ModelsViewNew } from './admin/ModelsViewNew';
import { RecycleBinView } from './admin/RecycleBinView';
import { ModelForm } from './admin/ModelForm';
import { AdminUsersView, AdminBillingView, AdminLogsView } from './admin/AdminBillingUsersView';
import { AdminPricingView } from './admin/AdminPricingView';
import { AdminSettingsView } from './admin/AdminSettingsView';
import adminService from '../services/adminService';
import mainLogo from '../assets/DAGGPT-01.png';

// Neumorphic Design System
const nm = {
  bg: '#e8ecf1',
  shadow: '8px 8px 16px #c5c9cd, -8px -8px 16px #ffffff',
  shadowSm: '4px 4px 8px #c5c9cd, -4px -4px 8px #ffffff',
  shadowInset: 'inset 4px 4px 8px #c5c9cd, inset -4px -4px 8px #ffffff',
  shadowLg: '12px 12px 24px #c5c9cd, -12px -12px 24px #ffffff',
};

export function AdminConsole() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [models, setModels] = useState([]);
  const [deletedModels, setDeletedModels] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [notification, setNotification] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');

  useEffect(() => {
    if (adminService.isAuthenticated()) {
      setIsAuthenticated(true);
      loadData();
    }
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [modelsData, statsData, deletedData] = await Promise.all([
        adminService.getAllModels(),
        adminService.getStats(),
        adminService.getDeletedModels()
      ]);

      // Transform models to include normalized 'function' field for Dashboard counts
      const transformedModels = (modelsData || []).map(model => {
        let functionType = 'Chat';
        if (model.category === 'video') {
          functionType = 'Video';
        } else if (model.category === 'music') {
          functionType = 'Music';
        } else if (model.category === 'image') {
          functionType = 'Image';
        } else if (model.category === 'chat') {
          functionType = 'Chat';
        } else if (model.functions && model.functions.includes('video')) {
          functionType = 'Video';
        } else if (model.functions && model.functions.includes('music')) {
          functionType = 'Music';
        } else if (model.functions && model.functions.includes('image')) {
          functionType = 'Image';
        }
        return { ...model, function: functionType };
      });

      setModels(transformedModels);
      setStats(statsData);
      setDeletedModels(deletedData);
    } catch (error) {
      showNotification('Failed to load data: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadDeletedModels = async () => {
    try {
      const deletedData = await adminService.getDeletedModels();
      setDeletedModels(deletedData);
    } catch (error) {
      showNotification('Failed to load deleted models: ' + error.message, 'error');
    }
  };

  const handleLogin = async (email, password) => {
    await adminService.login(email, password);
    setIsAuthenticated(true);
    loadData();
  };

  const handleLogout = () => {
    adminService.logout();
    setIsAuthenticated(false);
    setModels([]);
    setStats(null);
  };

  const handleAddModel = () => {
    setEditingModel(null);
    setShowForm(true);
  };

  const handleEditModel = (model) => {
    setEditingModel(model);
    setShowForm(true);
  };

  const handleSaveModel = async (modelData) => {
    try {
      if (editingModel) {
        await adminService.updateModel(editingModel.id, modelData);
        showNotification('Model updated successfully', 'success');
      } else {
        await adminService.createModel(modelData);
        showNotification('Model created successfully', 'success');
      }
      setShowForm(false);
      setEditingModel(null);
      await loadData();
      await adminService.reloadModels();
    } catch (error) {
      throw error;
    }
  };

  const handleToggleStatus = async (model) => {
    const newStatus = model.status === 'inactive' ? 'active' : 'inactive';
    const action = newStatus === 'active' ? 'enable' : 'disable';
    
    try {
      await adminService.updateModel(model.id, { ...model, status: newStatus });
      showNotification(`Model ${action}d successfully`, 'success');
      await loadData();
      await adminService.reloadModels();
    } catch (error) {
      showNotification(`Failed to ${action} model: ` + error.message, 'error');
    }
  };

  const handleDeleteModel = async (model) => {
    try {
      await adminService.deleteModel(model.id);
      showNotification('Model moved to recycle bin', 'success');
      await loadData();
      await adminService.reloadModels();
    } catch (error) {
      showNotification('Failed to delete model: ' + error.message, 'error');
    }
  };

  const handleRestoreModel = async (model) => {
    try {
      await adminService.restoreModel(model.id);
      showNotification('Model restored successfully', 'success');
      await loadData();
      await adminService.reloadModels();
    } catch (error) {
      showNotification('Failed to restore model: ' + error.message, 'error');
    }
  };

  const handlePermanentDelete = async (model) => {
    try {
      await adminService.permanentDeleteModel(model.id);
      showNotification('Model permanently deleted', 'success');
      await loadDeletedModels();
    } catch (error) {
      showNotification('Failed to permanently delete model: ' + error.message, 'error');
    }
  };

  const handleReload = async () => {
    try {
      await adminService.reloadModels();
      showNotification('Models reloaded successfully', 'success');
      await loadData();
    } catch (error) {
      showNotification('Failed to reload models: ' + error.message, 'error');
    }
  };

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  if (!isAuthenticated) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: nm.bg }}>
      {/* Left Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        onLogout={handleLogout}
        activeView={activeView}
        onViewChange={setActiveView}
      />

      {/* Main Content */}
      <div style={{ flex: 1, marginLeft: sidebarCollapsed ? '80px' : '260px', transition: 'margin-left 0.3s ease' }}>
        {/* Top Header */}
        <div style={{ background: nm.bg, borderBottom: '1px solid #d1d5db', padding: '20px 32px', boxShadow: nm.shadowSm }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', marginBottom: '4px' }}>
                {activeView === 'dashboard' && 'Dashboard Overview'}
                {activeView === 'models' && 'AI Models Management'}
                {activeView === 'users' && 'User Management'}
                {activeView === 'billing'  && 'Billing & Financial Reports'}
                {activeView === 'pricing'  && 'Model Pricing'}
                {activeView === 'analytics' && 'Analytics'}
                {activeView === 'logs' && 'Activity Logs'}
                {activeView === 'recycle-bin' && 'Recycle Bin'}
                {activeView === 'settings' && 'Settings'}
              </h1>
              <p style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
                {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* Search */}
              <div style={{ position: 'relative', display: 'none', '@media (min-width: 768px)': { display: 'block' } }}>
                <svg style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px' }} fill="#94a3b8" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"/></svg>
                <input
                  type="text"
                  placeholder="Quick search..."
                  style={{ paddingLeft: '40px', paddingRight: '16px', paddingTop: '10px', paddingBottom: '10px', width: '280px', background: nm.bg, border: 'none', borderRadius: '12px', fontSize: '14px', color: '#0f172a', boxShadow: nm.shadowInset, outline: 'none' }}
                />
              </div>

              {/* Notifications */}
              <button style={{ position: 'relative', padding: '10px', background: nm.bg, border: 'none', borderRadius: '12px', boxShadow: nm.shadowSm, cursor: 'pointer', transition: 'all 0.2s ease' }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = nm.shadow; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = nm.shadowSm; }}>
                <svg style={{ width: '20px', height: '20px' }} fill="#64748b" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"/></svg>
                <span style={{ position: 'absolute', top: '6px', right: '6px', width: '8px', height: '8px', background: '#ef4444', borderRadius: '50%' }} />
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div style={{ padding: '32px', overflowY: 'auto', minHeight: 'calc(100vh - 100px)' }}>
          <AnimatePresence mode="wait">
            {activeView === 'dashboard' && (
              <motion.div
                key="dashboard"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <DashboardViewNew stats={stats} models={models} />
              </motion.div>
            )}

            {activeView === 'models' && (
              <motion.div
                key="models"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <ModelsViewNew
                  models={models}
                  onToggleStatus={handleToggleStatus}
                  loading={loading}
                  onReload={handleReload}
                />
              </motion.div>
            )}

            {activeView === 'users' && (
              <motion.div key="users" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                <AdminUsersView />
              </motion.div>
            )}

            {activeView === 'billing' && (
              <motion.div key="billing" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                <AdminBillingView />
              </motion.div>
            )}

            {activeView === 'pricing' && (
              <motion.div key="pricing" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                <AdminPricingView />
              </motion.div>
            )}

            {activeView === 'analytics' && (
              <motion.div
                key="analytics"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-center py-20"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-indigo-500/30">
                  <svg className="w-10 h-10" fill="#fff" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"/></svg>
                </div>
                <h3 className="text-2xl font-black text-slate-900 mb-2">Analytics Coming Soon</h3>
                <p className="text-slate-500 font-medium">Advanced analytics and insights will be available here</p>
              </motion.div>
            )}

            {activeView === 'logs' && (
              <motion.div
                key="logs"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <AdminLogsView />
              </motion.div>
            )}

            {activeView === 'recycle-bin' && (
              <motion.div
                key="recycle-bin"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <RecycleBinView
                  deletedModels={deletedModels}
                  onRestore={handleRestoreModel}
                  onPermanentDelete={handlePermanentDelete}
                  onReload={loadDeletedModels}
                  loading={loading}
                />
              </motion.div>
            )}

            {activeView === 'settings' && (
              <motion.div
                key="settings"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <AdminSettingsView />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Model Form Modal */}
      <AnimatePresence>
        {showForm && (
          <ModelForm
            model={editingModel}
            onSave={handleSaveModel}
            onCancel={() => {
              setShowForm(false);
              setEditingModel(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* Notification Toast */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-6 right-6 z-50"
          >
            <div className={`px-6 py-4 rounded-xl shadow-2xl border-2 ${
              notification.type === 'success'
                ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                : notification.type === 'error'
                ? 'bg-red-50 border-red-300 text-red-700'
                : 'bg-blue-50 border-blue-300 text-blue-700'
            }`}>
              <p className="font-bold">{notification.message}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
